const CONFIGURED_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
// Deployed AION uses the same-origin nginx /api proxy. This keeps the
// HttpOnly session cookie first-party, including Voice/STT/TTS requests.
// Local development may still point directly at a configured backend.
const BACKEND_URL = process.env.NODE_ENV === "production" ? "" : CONFIGURED_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const localizeMessage = (locale, messages) => {
  const base = String(locale || "de").toLowerCase().split(/[-_]/)[0];
  return messages[base] || messages.en || messages.de;
};

const serviceUnavailableMessage = (locale) => localizeMessage(locale, {
  de: "AION ist gerade kurz nicht erreichbar. Bitte versuche es gleich noch einmal.",
  en: "AION is briefly unavailable right now. Please try again in a moment.",
});

const fallbackMessageForStatus = (status, fallback, locale = "de") => {
  if (status === 401) return localizeMessage(locale, { de: "Ungültige E-Mail oder Passwort.", en: "Invalid email or password." });
  if (status === 429) return localizeMessage(locale, { de: "Zu viele Anfragen in kurzer Zeit. Bitte kurz warten und dann erneut versuchen.", en: "Too many requests in a short time. Please wait a moment and try again." });
  if ([502, 503, 504].includes(status)) return serviceUnavailableMessage(locale);
  if (status >= 500) return localizeMessage(locale, { de: "Der Dienst antwortet gerade nicht stabil. Bitte versuche es gleich erneut.", en: "The service is not responding reliably right now. Please try again shortly." });
  return fallback;
};

const friendlyErrorMessage = (detail, fallback, { status, locale = "de" } = {}) => {
  const detailText = typeof detail === "string" ? detail.trim() : detail;
  if (detail === "Authentication email delivery is not configured" || detail === "Authentication email could not be delivered") {
    return localizeMessage(locale, {
      de: "Die E-Mail-Bestätigung ist noch nicht eingerichtet. Die Registrierung wurde nicht gespeichert. Bitte versuche es später erneut.",
      en: "Email verification is not configured yet. Your registration was not saved. Please try again later.",
    });
  }
  if ([502, 503, 504].includes(status)) {
    return fallbackMessageForStatus(status, fallback, locale);
  }
  if (typeof detailText === "string" && /<(?:!doctype|html|body|head|script|div|title)[\s>]/i.test(detailText)) {
    return fallbackMessageForStatus(status || 500, fallback, locale);
  }
  if (detail === "API abuse detected" || detail === "Rate limit exceeded") {
    return fallbackMessageForStatus(429, fallback, locale);
  }
  if (detail === "Service temporarily protected by circuit breaker") {
    return localizeMessage(locale, {
      de: "Der Dienst ist gerade kurz geschützt. Bitte warte einen Moment und versuche die Anmeldung erneut.",
      en: "The service is briefly protected right now. Please wait a moment and try signing in again.",
    });
  }
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || item?.detail || item).filter(Boolean).join(" ") || fallback;
  }
  if (detail && typeof detail === "object") {
    return detail.msg || detail.detail || fallback;
  }
  return detail || fallback;
};

const buildRequestError = async (response, fallback, options = {}) => {
  const raw = await response.text().catch(() => "");
  let payload = null;
  if (raw) {
    try {
      payload = JSON.parse(raw);
    } catch {
      payload = { detail: raw };
    }
  }
  const error = new Error(
    friendlyErrorMessage(
      payload?.detail,
      fallbackMessageForStatus(response.status, fallback, options.locale),
      { status: response.status, locale: options.locale },
    ),
  );
  error.status = response.status;
  return error;
};

const jsonFetchWithTimeout = async (path, { method = "POST", payload, token, locale = "de", fallback, timeoutMs = 18000 } = {}) => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${API}${path}`, {
      method,
      headers: {
        ...(payload !== undefined ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: "include",
      body: payload === undefined ? undefined : JSON.stringify(payload),
      signal: controller.signal,
    });

    if (!response.ok) {
      throw await buildRequestError(response, fallback, { locale });
    }

    return response.json();
  } catch (error) {
    if (error?.name === "AbortError") {
      const timeoutError = new Error(localizeMessage(locale, {
        de: "AION braucht gerade zu lange. Bitte versuche es erneut.",
        en: "AION is taking too long right now. Please try again.",
      }));
      timeoutError.status = 408;
      throw timeoutError;
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
};

const request = async (path, token) => {
  const response = await fetch(`${API}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });

  if (!response.ok) {
    throw await buildRequestError(response, `Request failed: ${response.status}`);
  }

  return response.json();
};

const jsonRequest = async (path, method, payload, token) => {
  const response = await fetch(`${API}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: payload === undefined ? undefined : JSON.stringify(payload),
  });

  if (!response.ok) {
    throw await buildRequestError(response, `Request failed: ${response.status}`);
  }

  return response.json();
};

export const loginAccount = async (email, password, portal) => {
  const response = await fetch(`${API}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      email,
      password,
      ...(portal ? { portal } : {}),
    }),
  });

  if (!response.ok) {
    throw await buildRequestError(response, "Login failed");
  }

  return response.json();
};

export const loginAdmin = async (email, password) => loginAccount(email, password, "admin");
export const getRegistrationStatus = async () => request("/auth/registration-status");
export const getBootstrapAdminStatus = async () => request("/auth/bootstrap-admin/status");
export const establishBootstrapAdminSession = async (bootstrapToken) => jsonFetchWithTimeout("/auth/bootstrap-admin/session", {
  method: "POST",
  payload: { bootstrap_token: bootstrapToken },
  fallback: "Der sichere Admin-Setup-Link konnte nicht aktiviert werden.",
  timeoutMs: 8000,
});
export const bootstrapPlatformAdmin = async (payload) => jsonFetchWithTimeout("/auth/bootstrap-admin", { method: "POST", payload, fallback: "Erst-Admin konnte nicht erstellt werden.", timeoutMs: 12000 });
export const registerAccount = async (payload) => jsonRequest("/auth/register", "POST", payload);
export const verifyEmailToken = async (payload) => jsonRequest("/auth/verify-email", "POST", payload);
export const resendVerification = async (payload) => jsonRequest("/auth/resend-verification", "POST", payload);
export const requestPasswordReset = async (payload) => jsonRequest("/auth/forgot-password", "POST", payload);
export const resetPasswordWithToken = async (payload) => jsonRequest("/auth/reset-password", "POST", payload);
export const acceptCustomerOwnerInvitation = async (payload) => jsonFetchWithTimeout("/auth/customer-owner-invitations/accept", {
  method: "POST",
  payload,
  fallback: "Customer-Owner-Einladung konnte nicht angenommen werden.",
  timeoutMs: 12000,
});
export const getCurrentUser = async () => request("/auth/me");
export const logoutAccount = async (token) => jsonRequest("/auth/logout", "POST", {}, token);
export const getAuthSessions = async (token) => request("/auth/sessions", token);
export const revokeAuthSession = async (sessionId, payload, token) => jsonRequest(`/auth/sessions/${sessionId}/revoke`, "POST", payload, token);

export const getSystemStatus = async (token) => request("/status", token);
export const getAutonomyProfile = async (token) => request("/autonomy/profile", token);
export const getAutomations = async (token) => request("/automations", token);
export const createAutomation = async (payload, token) => jsonRequest("/automations", "POST", payload, token);
export const setAutomationEnabled = async (ruleId, enabled, token) => jsonRequest(`/automations/${encodeURIComponent(ruleId)}/enabled`, "PATCH", { enabled }, token);
export const deleteAutomation = async (ruleId, token) => jsonRequest(`/automations/${encodeURIComponent(ruleId)}`, "DELETE", undefined, token);
export const getRooms = async (token) => request("/rooms", token);
export const getHealth = async (token) => request("/system/health", token);
export const getConsciousness = async (token) => request("/system/consciousness/status", token);
export const getLocalCoreStatus = async (token) => request("/local-core/status", token);
export const getHardwareDevices = async (token) => request("/hardware/devices", token);
export const getHardwarePhysicalHub = async (token) => request("/hardware/physical-hub", token);
export const getHardwareConnectors = async (token) => request("/hardware/connectors", token);
export const getAionDevices = async (token) => request("/aion-devices", token);
export const getAionDevicesOverview = async (token) => request("/aion-devices/overview", token);
export const getAionDeviceSessions = async (token) => request("/aion-devices/sessions", token);
export const getDevicesOverview = async (token) => request("/control/devices", token);
export const getSpatialRoomCameraInsights = async (roomId, token, limit = 3) => request(`/camera/spatial-room-insights?room_id=${encodeURIComponent(roomId)}&limit=${Math.max(1, Math.min(Number(limit) || 3, 5))}`, token);
export const getUnifiedDashboard = async (token) => request("/control/dashboard", token);
export const getObservabilityOverview = async (token) => request("/observability/overview", token);
export const getAlerts = async (token) => request("/observability/alerts/summary", token);
export const getAgents = async (token) => request("/agents", token);
export const getPredictions = async (token) => request("/system/predictions", token);
export const getDecisionLog = async (token) => request("/system/consciousness/decisions", token);
export const getMemory = async (userId, token) => request(`/memory/${userId}`, token);
export const getSuggestions = async (userId, token) => request(`/memory/${userId}/suggestions`, token);
export const getFamilyProfiles = async (token) => request("/family/profiles", token);
export const getTrustCenter = async (token) => {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(`${API}/trust/center`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
      cache: "no-store",
      signal: controller.signal,
    });

    const raw = await response.text();
    const payload = raw ? JSON.parse(raw) : null;

    if (!response.ok) {
      const error = new Error(friendlyErrorMessage(payload?.detail, `Request failed: ${response.status}`));
      error.status = response.status;
      throw error;
    }

    return payload;
  } catch (error) {
    if (error?.name === "AbortError") {
      const timeoutError = new Error("Trust Center request timed out.");
      timeoutError.status = 408;
      throw timeoutError;
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
};
export const updateTrustSettings = async (payload, token) => jsonRequest("/trust/settings", "POST", payload, token);
export const updateTrustAutonomy = async (payload, token) => jsonRequest("/trust/autonomy/profile", "POST", payload, token);
export const setTrustAionStop = async (payload, token) => jsonRequest("/trust/aion-stop", "POST", payload, token);
export const createTrustMemoryGrant = async (payload, token) => jsonRequest("/trust/memory/grants", "POST", payload, token);
export const revokeTrustMemoryGrant = async (grantId, payload, token) => jsonRequest(`/trust/memory/grants/${grantId}/revoke`, "POST", payload, token);
export const revokeTrustAuthSession = async (sessionId, payload, token) => jsonRequest(`/trust/auth-sessions/${sessionId}/revoke`, "POST", payload, token);
export const revokeOtherTrustAuthSessions = async (payload, token) => jsonRequest("/trust/auth-sessions/revoke-others", "POST", payload, token);
export const revokeTrustClientSession = async (sessionId, payload, token) => jsonRequest(`/trust/client-sessions/${sessionId}/revoke`, "POST", payload, token);
export const switchFamilyProfile = async (targetUserId, token) => {
  const response = await fetch(`${API}/family/profiles/switch`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify({ target_user_id: targetUserId }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(friendlyErrorMessage(body.detail, "Profile switch failed"));
  }

  return response.json();
};
export const updateMemoryPreferences = async (payload, token) => {
  const response = await fetch(`${API}/memory/preferences`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(friendlyErrorMessage(body.detail, "Memory update failed"));
  }

  return response.json();
};

export const sendVoiceCommand = async (payload, token, locale = "de") => {
  return jsonFetchWithTimeout("/voice", {
    method: "POST",
    payload,
    token,
    locale,
    fallback: serviceUnavailableMessage(locale),
    timeoutMs: 22000,
  });
};

export const getVoiceStatus = async (token, sessionId) => request(`/voice/status${sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : ""}`, token);

export const syncVoiceState = async (payload, token) => (
  jsonFetchWithTimeout("/voice/state", {
    method: "POST",
    payload,
    token,
    fallback: "Voice state sync failed",
    timeoutMs: 6000,
  })
);

export const interruptVoiceSession = async (payload, token) => (
  jsonFetchWithTimeout("/voice/interrupt", {
    method: "POST",
    payload,
    token,
    fallback: "Voice interruption failed",
    timeoutMs: 6000,
  })
);

export const startVoiceConversation = async (payload, token, locale = "de") => {
  return jsonFetchWithTimeout("/voice/conversation", {
    method: "POST",
    payload,
    token,
    locale,
    fallback: serviceUnavailableMessage(locale),
    timeoutMs: 18000,
  });
};

export const transcribeVoiceAudio = async (payload, token, locale = "de") => {
  return jsonFetchWithTimeout("/voice/transcribe", {
    method: "POST",
    payload,
    token,
    locale,
    fallback: serviceUnavailableMessage(locale),
    timeoutMs: 18000,
  });
};

export const speakVoiceText = async (payload, token, locale = "de") => {
  return jsonFetchWithTimeout("/voice/speak", {
    method: "POST",
    payload,
    token,
    locale,
    fallback: serviceUnavailableMessage(locale),
    timeoutMs: 12000,
  });
};

export const searchMemory = async (payload, token) => {
  const response = await fetch(`${API}/memory/search`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(friendlyErrorMessage(body.detail, "Memory search failed"));
  }

  return response.json();
};

export const recallMemory = async (payload, token) => {
  const response = await fetch(`${API}/memory/recall`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(friendlyErrorMessage(body.detail, "Memory recall failed"));
  }

  return response.json();
};

export const getMemoryTopics = async (token, profileUserId) => request(`/memory/topics${profileUserId ? `?profile_user_id=${encodeURIComponent(profileUserId)}` : ""}`, token);
export const getConversationHistory = async (token, profileUserId, sessionLimit = 20) => request(`/memory/conversations?session_limit=${Math.max(1, Math.min(Number(sessionLimit) || 20, 50))}${profileUserId ? `&profile_user_id=${encodeURIComponent(profileUserId)}` : ""}`, token);

export const registerAionDevice = async (payload, token) => {
  const response = await fetch(`${API}/aion-devices`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(friendlyErrorMessage(body.detail, "AION client registration failed"));
  }

  return response.json();
};

export const openAionDeviceSession = async (deviceId, payload, token) => {
  const response = await fetch(`${API}/aion-devices/${deviceId}/sessions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify(payload || {}),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(friendlyErrorMessage(body.detail, "AION session start failed"));
  }

  return response.json();
};

export const markAionDeviceLost = async (deviceId, token) => {
  const response = await fetch(`${API}/aion-devices/${deviceId}/lost`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(friendlyErrorMessage(body.detail, "AION device lost mode failed"));
  }

  return response.json();
};

export const revokeAionDevice = async (deviceId, token) => {
  const response = await fetch(`${API}/aion-devices/${deviceId}/revoke`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(friendlyErrorMessage(body.detail, "AION device revoke failed"));
  }

  return response.json();
};

export const factoryResetAionDevice = async (deviceId, token) => {
  const response = await fetch(`${API}/aion-devices/${deviceId}/factory-reset`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify({ confirmed: true }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(friendlyErrorMessage(body.detail, "AION device factory reset failed"));
  }

  return response.json();
};

export const getPlatformCustomers = async (token) => request("/platform/customers", token);
export const getPlatformCustomerDetail = async (tenantId, token) => request(`/platform/customers/${tenantId}`, token);
export const createPlatformCustomer = async (payload, token) => jsonRequest("/platform/customers", "POST", payload, token);
export const updatePlatformCustomer = async (tenantId, payload, token) => jsonRequest(`/platform/customers/${tenantId}`, "PATCH", payload, token);
export const getPlatformHome = async (token) => request("/platform/home", token);
export const createPlatformLocation = async (tenantId, payload, token) => jsonRequest(`/platform/customers/${tenantId}/locations`, "POST", payload, token);
export const updatePlatformLocation = async (tenantId, locationId, payload, token) => jsonRequest(`/platform/customers/${tenantId}/locations/${locationId}`, "PATCH", payload, token);
export const getPlatformInstallations = async (token, tenantId) => request(`/platform/installations${tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : ""}`, token);
export const createPlatformInstallation = async (payload, token) => jsonRequest("/platform/installations", "POST", payload, token);
export const updatePlatformInstallation = async (installationId, tenantId, payload, token) => jsonRequest(`/platform/installations/${installationId}?tenant_id=${encodeURIComponent(tenantId)}`, "PATCH", payload, token);
export const getPlatformInstallationDetail = async (installationId, tenantId, token) => request(`/platform/installations/${installationId}?tenant_id=${encodeURIComponent(tenantId)}`, token);
export const recordPlatformInstallationHealthTest = async (installationId, payload, token) => jsonRequest(`/platform/installations/${installationId}/health-test`, "POST", payload, token);
export const createPlatformCustomerOwnerInvitation = async (installationId, payload, token) => jsonRequest(`/platform/installations/${installationId}/customer-owner-invitations`, "POST", payload, token);
export const revokePlatformCustomerOwnerInvitation = async (installationId, invitationId, payload, token) => jsonRequest(
  `/platform/installations/${installationId}/customer-owner-invitations/${invitationId}/revoke`,
  "POST",
  payload,
  token,
);
export const confirmPlatformInstallationHandover = async (installationId, payload, token) => jsonRequest(`/platform/installations/${installationId}/handover/confirm`, "POST", payload, token);
export const enrollPlatformCore = async (installationId, payload, token) => jsonRequest(`/platform/installations/${installationId}/core-enrollment`, "POST", payload, token);
export const getPlatformCores = async (token, tenantId) => request(`/platform/cores${tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : ""}`, token);
export const testPlatformCore = async (installationId, tenantId, token) => jsonRequest(`/platform/installations/${installationId}/core/test?tenant_id=${encodeURIComponent(tenantId)}`, "POST", {}, token);
export const updatePlatformCore = async (installationId, payload, token) => jsonRequest(`/platform/installations/${installationId}/core/update`, "POST", payload, token);
export const setPlatformCoreMaintenance = async (installationId, payload, token) => jsonRequest(`/platform/installations/${installationId}/core/maintenance`, "POST", payload, token);
export const replacePlatformCore = async (installationId, payload, token) => jsonRequest(`/platform/installations/${installationId}/core/replace`, "POST", payload, token);
export const revokePlatformCore = async (installationId, tenantId, token) => jsonRequest(`/platform/installations/${installationId}/core/revoke?tenant_id=${encodeURIComponent(tenantId)}`, "POST", {}, token);
export const getPlatformRooms = async (token, { tenantId, installationId } = {}) => request(`/platform/rooms${tenantId || installationId ? `?${new URLSearchParams({ ...(tenantId ? { tenant_id: tenantId } : {}), ...(installationId ? { installation_id: installationId } : {}) }).toString()}` : ""}`, token);
export const createPlatformRoom = async (payload, token) => jsonRequest("/platform/rooms", "POST", payload, token);
export const getPlatformUsers = async (token, tenantId) => request(`/platform/users${tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : ""}`, token);
export const createPlatformUser = async (payload, token) => jsonRequest("/platform/users", "POST", payload, token);
export const updatePlatformUser = async (userId, payload, token) => jsonRequest(`/platform/users/${userId}`, "PATCH", payload, token);
export const getPlatformEmployees = async (token) => request("/platform/employees", token);
export const createPlatformEmployee = async (payload, token) => jsonRequest("/platform/employees", "POST", payload, token);
export const updatePlatformEmployee = async (employeeId, payload, token) => jsonRequest(`/platform/employees/${employeeId}`, "PATCH", payload, token);
export const getPlatformRoles = async (token) => request("/platform/roles", token);
export const getPlatformHardwareDiscovery = async (token) => request("/platform/hardware/discovery", token);
export const getPlatformHardware = async (token, { tenantId, installationId } = {}) => request(`/platform/hardware${tenantId || installationId ? `?${new URLSearchParams({ ...(tenantId ? { tenant_id: tenantId } : {}), ...(installationId ? { installation_id: installationId } : {}) }).toString()}` : ""}`, token);
export const enrollPlatformHardware = async (payload, token) => jsonRequest("/platform/hardware/enroll", "POST", payload, token);
export const configurePlatformHardware = async (deviceId, tenantId, config, token) => jsonRequest(`/platform/hardware/${deviceId}/configure?tenant_id=${encodeURIComponent(tenantId)}`, "POST", { config }, token);
export const testPlatformHardware = async (deviceId, tenantId, token) => jsonRequest(`/platform/hardware/${deviceId}/test?tenant_id=${encodeURIComponent(tenantId)}`, "POST", {}, token);
export const restartPlatformHardware = async (deviceId, tenantId, token) => jsonRequest(`/platform/hardware/${deviceId}/restart?tenant_id=${encodeURIComponent(tenantId)}`, "POST", {}, token);
export const movePlatformHardware = async (deviceId, payload, token) => jsonRequest(`/platform/hardware/${deviceId}/move-room`, "POST", payload, token);
export const updatePlatformHardware = async (deviceId, payload, token) => jsonRequest(`/platform/hardware/${deviceId}/update`, "POST", payload, token);
export const revokePlatformHardware = async (deviceId, tenantId, token) => jsonRequest(`/platform/hardware/${deviceId}/revoke?tenant_id=${encodeURIComponent(tenantId)}`, "POST", {}, token);
export const replacePlatformHardware = async (deviceId, payload, token) => jsonRequest(`/platform/hardware/${deviceId}/replace`, "POST", payload, token);
export const getPlatformSystemSummary = async (token, tenantId) => request(`/platform/system/summary${tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : ""}`, token);
export const getPlatformDashboard = async (token) => request("/platform/dashboard", token);
export const getPlatformGoLiveHealth = async (installationId, tenantId, token) => request(`/platform/installations/${installationId}/go-live-health?tenant_id=${encodeURIComponent(tenantId)}`, token);
export const getPlatformBackups = async (installationId, tenantId, token) => request(`/platform/installations/${installationId}/backups?tenant_id=${encodeURIComponent(tenantId)}`, token);
export const createPlatformBackup = async (installationId, payload, token) => jsonRequest(`/platform/installations/${installationId}/backups`, "POST", payload, token);
export const verifyPlatformBackup = async (installationId, backupId, payload, token) => jsonRequest(`/platform/installations/${installationId}/backups/${backupId}/verify`, "POST", payload, token);
export const deletePlatformBackup = async (installationId, backupId, tenantId, token) => jsonRequest(`/platform/installations/${installationId}/backups/${backupId}?tenant_id=${encodeURIComponent(tenantId)}`, "DELETE", undefined, token);
export const restorePlatformBackup = async (installationId, payload, token) => jsonRequest(`/platform/installations/${installationId}/restore`, "POST", payload, token);
export const recoverPlatformCore = async (installationId, payload, token) => jsonRequest(`/platform/installations/${installationId}/core/recovery`, "POST", payload, token);
export const getPlatformOTAPackages = async (token) => request("/platform/updates/packages", token);
export const createPlatformOTAPackage = async (payload, token) => jsonRequest("/platform/updates/packages", "POST", payload, token);
export const updatePlatformReleaseChannel = async (payload, token) => jsonRequest("/platform/updates/channel", "POST", payload, token);
export const deployPlatformOTA = async (payload, token) => jsonRequest("/platform/updates/deploy", "POST", payload, token);
export const getPlatformMigrations = async (installationId, tenantId, token) => request(`/platform/installations/${installationId}/migrations?tenant_id=${encodeURIComponent(tenantId)}`, token);
export const applyPlatformMigration = async (installationId, payload, token) => jsonRequest(`/platform/installations/${installationId}/migrations`, "POST", payload, token);
export const getPlatformPhase15Summary = async (token, tenantId) => request(`/platform/phase15/summary${tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : ""}`, token);
export const acknowledgePlatformDeadLetter = async (eventId, payload, token) => jsonRequest(`/platform/local-core/dead-letters/${eventId}/acknowledge`, "POST", payload, token);
export const retryPlatformDeadLetter = async (eventId, payload, token) => jsonRequest(`/platform/local-core/dead-letters/${eventId}/retry`, "POST", payload, token);
export const acknowledgePlatformConflict = async (conflictId, payload, token) => jsonRequest(`/platform/local-core/conflicts/${conflictId}/acknowledge`, "POST", payload, token);
export const retryPlatformConflict = async (conflictId, payload, token) => jsonRequest(`/platform/local-core/conflicts/${conflictId}/retry`, "POST", payload, token);
export const retryPlatformFailedSync = async (eventId, payload, token) => jsonRequest(`/platform/local-core/failed-sync/${eventId}/retry`, "POST", payload, token);
export const getOpenAIProviderSettings = async (token) => request("/platform/providers/openai", token);
export const saveOpenAIProviderKey = async (apiKey, token) => jsonRequest("/platform/providers/openai", "PUT", { api_key: apiKey }, token);
export const testOpenAIProvider = async (token) => jsonRequest("/platform/providers/openai/test", "POST", {}, token);
export const removeOpenAIProviderKey = async (token) => jsonRequest("/platform/providers/openai", "DELETE", undefined, token);
export const getSmtpProviderSettings = async (token) => request("/platform/providers/smtp", token);
export const saveSmtpProviderSettings = async (payload, token) => jsonRequest("/platform/providers/smtp", "PUT", payload, token);
export const testSmtpProvider = async (token) => jsonRequest("/platform/providers/smtp/test", "POST", {}, token);
export const removeSmtpProviderSettings = async (token) => jsonRequest("/platform/providers/smtp", "DELETE", undefined, token);
